import { EvaluationCalendarPicker } from 'components/CalendarPicker/EvaluationCalendarPicker';
import { EvaluationDuration } from 'domains/calendars/types';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useSignupEvaluationCalendar } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import {
  EvaluationScheduleFormType,
  SingleEvaluationSchema,
  SingleEvaluationSchemaType,
} from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { useCallback, useEffect } from 'react';
import { useFormContext, FormProvider, useForm } from 'react-hook-form';
import { useTrack } from 'utils/hooks/useMixPanel';

import { Button, Callout, FormLayout, Heading, LoadingDots, Paragraph, SubmitButton } from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import { useTranslation } from 'react-i18next';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { useNavigate } from 'react-router-dom';
import { routes } from 'router/routes';
import { useSignupRoutingRules } from 'domains/signupDirect/businessLogic/hooks';
import { useMinimumAvailability } from 'domains/signupDirect/misc/useMinimumAvailability';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { trackTimeOptionsSelection } from 'domains/signupDirect/Evaluation/Schedule/TimeOptions/trackTimeOptions';
import { TimeRange, timeRangeValues } from 'domains/signupDirect/misc/constants';
import { SignupConsultationDualBailout } from 'domains/signupDirect/misc/SignupConsultationDualBailout';
import { useOnBailoutDueToAvailability, useOnBailoutDueToQuestion } from 'domains/signupDirect/misc/jotform/hooks';

export const Component = () => {
  useAutoScrollToTop();
  useSignupNavigationGuard();
  const { filters, onContinueToEvalForm } = useSignupDirectCtx();
  if (!filters) throw new Error('Expected filters to be set');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { track } = useTrack();
  const onBailoutDueToAvailability = useOnBailoutDueToAvailability();
  const onBailoutDueToQuestion = useOnBailoutDueToQuestion();

  const { signupBookingCopiesUpdate, signupShowTimeOptions: shouldShowTimeOptions } = useFlags();

  const methods = useForm<SingleEvaluationSchemaType>({
    resolver: zodResolver(SingleEvaluationSchema),
    defaultValues: {
      type: 'single',
      timeOptions: {
        earlyMorning: false,
        morning: false,
        afternoon: false,
        evening: false,
      },
    },
  });

  const { handleSubmit } = methods;
  const { isSplitEvaluationAllowed } = useSignupRoutingRules();
  const canScheduleSplitEvaluations = isSplitEvaluationAllowed.allowed;

  const { evaluationCalendarQuery } = useSignupEvaluationCalendar(filters, EvaluationDuration.Single);
  const { isLoading } = evaluationCalendarQuery;
  const { isRedirecting } = useMinimumAvailability({
    evaluationCalendarQuery,
  });

  const pickedTimeSlot = methods.watch('timeSlot');

  const onSwitchToSplits = useCallback(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.BAILOUT_TO_SINGLE, {
      step: DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.BAILOUT_TO_SPLITS,
    });
    navigate(routes.signupDirect.evaluation.schedule.split.experimentFork);
  }, []);

  if (isLoading || isRedirecting) {
    return <LoadingDots className="flex flex-col h-max static mt-48" message={t('directSignup.schedule.loading')} />;
  }

  const onSubmitEvaluationForm = (data: SingleEvaluationSchemaType) => {
    const selectedTimeOptions: TimeRange[] | undefined =
      shouldShowTimeOptions && data.timeOptions ? timeRangeValues.filter(key => data.timeOptions?.[key]) : undefined;

    trackTimeOptionsSelection(selectedTimeOptions, track);

    onContinueToEvalForm(data);
  };

  return (
    <>
      <BookingCopy
        canScheduleSplitEvaluations={canScheduleSplitEvaluations}
        onSwitchToSplits={onSwitchToSplits}
        signupBookingCopiesUpdate={signupBookingCopiesUpdate}
        t={t}
      />
      <FormProvider {...methods}>
        <FormLayout
          className="mobile-xs-d:gap-e40 tablet-d:gap-e56 pb-e128"
          onSubmit={handleSubmit(onSubmitEvaluationForm)}
        >
          <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
            <FormFields />

            {!pickedTimeSlot && (
              <SignupConsultationDualBailout
                onBailoutToConsultDueToAvailability={onBailoutDueToAvailability}
                onBailoutToConsultDueToQuestion={onBailoutDueToQuestion}
              />
            )}

            {filters.therapyTarget === 'child' && (
              <Callout className="w-full" type="info">
                {t('directSignup.schedule.childNote')}
              </Callout>
            )}
          </FormLayout.Fields>

          <FormLayout.CTA sticky className="flex mobile-d:flex-col w-full">
            <SubmitButton data-testid="picktime-submit-button">{t('directSignup.eval.schedule.submit')}</SubmitButton>
          </FormLayout.CTA>
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields = () => {
  const { filters, isResumedLead } = useSignupDirectCtx();

  const { signupShowTimeOptions: shouldShowTimeOptions } = useFlags();

  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.SCHEDULE.SINGLE, { isResumedLead });
  if (!filters) {
    throw 'Expected filters to be set';
  }

  const { displayTimezone } = useDisplayTimezone();

  const { setValue } = useFormContext<EvaluationScheduleFormType>();

  const data = useSignupEvaluationCalendar(filters, EvaluationDuration.Single);

  useEffect(() => {
    if (displayTimezone) setValue('timeZone', displayTimezone.resolvedName);
  }, [displayTimezone]);

  if (data.isLoading) {
    throw new Error('Expected data to be loaded Single Eval fields');
  }

  return (
    <EvaluationCalendarPicker
      calendarData={data}
      name="timeSlot"
      therapistBadgesData={{
        clinicalAreas: filters.clinicalAreas,
        insuranceName:
          filters.paymentMethod.type === 'insurance' ? filters.paymentMethod.insuranceData.displayName : undefined,
        stateCode: filters.stateCode,
      }}
      shouldShowTimeOptions={shouldShowTimeOptions}
    />
  );
};

interface BookingCopyProps {
  canScheduleSplitEvaluations: boolean;
  onSwitchToSplits: () => void;
  signupBookingCopiesUpdate: boolean;
  t: (key: string) => string;
}

/* Returns a component that displays different booking copies based on the signupBookingCopiesUpdate flag. */
const BookingCopy: React.FC<BookingCopyProps> = ({
  canScheduleSplitEvaluations,
  onSwitchToSplits,
  signupBookingCopiesUpdate,
  t,
}) => {
  if (signupBookingCopiesUpdate) {
    return (
      <div>
        <Heading level="h1">{t('directSignup.schedule.single.bookingTitle')}</Heading>
        <Paragraph size="lg">{t('directSignup.schedule.single.bookingSubtitle.1')}</Paragraph>
        <Paragraph size="lg">{t('directSignup.schedule.single.bookingSubtitle.2')}</Paragraph>
        <Paragraph size="lg">
          {canScheduleSplitEvaluations && (
            <>
              <span>{t('directSignup.schedule.single.splitSellout')}</span>
              <Button
                as="a"
                role="button"
                variant="link"
                tabIndex={0}
                onClick={onSwitchToSplits}
                className="text-left inline"
              >
                {t('directSignup.schedule.single.switchToSplit')}
              </Button>
            </>
          )}
        </Paragraph>
        <Callout type="info">{t('directSignup.schedule.virtualTherapySellout')}</Callout>
      </div>
    );
  }

  return (
    <div>
      <Heading level="h1">{t('directSignup.schedule.single.title')}</Heading>
      <Paragraph size="lg" noDefaultMargins>
        {t('directSignup.schedule.single.subTitle')}
        {canScheduleSplitEvaluations && (
          <>
            {' '}
            {t('directSignup.schedule.single.splitSellout.1')}
            <Button
              as="a"
              role="button"
              variant="link"
              tabIndex={0}
              onClick={onSwitchToSplits}
              className="text-left inline"
            >
              {t('directSignup.schedule.single.splitSellout.2')}
            </Button>
          </>
        )}
      </Paragraph>
    </div>
  );
};

export const DirectSignupPickTimeSlot = Component;
